import { lazy } from "react";
import {
  operarioNavItems,
  adminNavItems,
  contabilidadeNavItems,
  recargatestNavItems,
  irsNavItems,
} from "./layout/navConfig";
import { getSession } from "../security/session";
import { primaryRole } from "../security/roles";

/**
 * routeConfig.js
 *
 * Single source of truth for every role's navigation and routes.
 * Dashboard.jsx reads this to decide which nav to show and which
 * routes to register — no more one-file-per-role.
 */

// ── feature components ────────────────────────────────────────────────────────

// clients
const RegistarCliente = lazy(() => import("./clients/RegistarCliente"));
const Pesquisarcliente = lazy(() => import("./clients/pesquisarcliente"));
const ListarCliente = lazy(() => import("./clients/ListarCliente"));
const ClienteRegistro = lazy(() => import("./clients/ClienteRegistro"));
const ClientesDescontoPage = lazy(() => import("../pages/ClientesDescontoPage"));
const ClientesIsencaoPage = lazy(() => import("../pages/ClientesIsencaoPage"));

// meters
const Importarcontador = lazy(() => import("./meters/importarcontador"));
const Pesquisarcontador = lazy(() => import("./meters/pesquisarcontador"));
const ListaContador = lazy(() => import("./meters/ListaContador"));

// sales
const GestVendas = lazy(() => import("./sales/GestVendas"));
const AGestaVendas = lazy(() => import("./sales/AGestaVendas"));
const ComprasFeitas = lazy(() => import("./sales/ComprasFeitas"));
const ComprasFeitasVitae = lazy(() => import("./sales/ComprsaFeitasV"));
const ComprasFeitasTictac = lazy(() => import("./sales/ComprasFeitasTictac"));
const RecargasEmitidas = lazy(() => import("./admin/RecargasEmitidas"));
const ComprarToken = lazy(() => import("./sales/ComprarToken"));
const HRelatorioVenda = lazy(() => import("./sales/HRelatorioVenda"));
const RelatorioVenda = lazy(() => import("./sales/RelatorioVenda"));
const PipeLine = lazy(() => import("./sales/PipeLine"));

// payments
const GestPagamentos = lazy(() => import("./payments/GestPagamentos"));
const AGestPagamentos = lazy(() => import("./payments/AGestPagamentos"));
const Geststs = lazy(() => import("./payments/geststs"));

// admin
const AdminUsers = lazy(() => import("./admin/AdminUsers"));
const FixedFeeNeighborhoods = lazy(() => import("./admin/FixedFeeNeighborhoods"));
const AdminTaxSettings = lazy(() => import("./admin/AdminTaxSettings"));
const MeterBrands = lazy(() => import("./admin/MeterBrands"));

// reports
const GestRelatorios = lazy(() => import("./reports/GestRelatorios"));
const AGestRelatorios = lazy(() => import("./reports/AGestRelatorios"));
const AdminReportsCenter = lazy(() => import("./reports/AdminReportsCenter"));
const SalesReportsHub = lazy(() => import("./reports/SalesReportsHub"));
const GestaoEstatisc = lazy(() => import("./reports/GestaoEstatisc"));
const HGestaoEstatisc = lazy(() => import("./reports/HGestaoEstatisc"));
const VolumeConsumido = lazy(() => import("./reports/VolumeConsumido"));
const Montanteadq = lazy(() => import("./reports/Montanteadq"));
const ConsumoCliente = lazy(() => import("./reports/consumocliente"));
const ConsumoExcessivo = lazy(() => import("./reports/ConsumoExcessivo"));
const ConsumoInadequado = lazy(() => import("./reports/ConsumoInadequado"));
const SemConsumo = lazy(() => import("./reports/SemConsumo"));
const RollOver = lazy(() => import("./reports/RollOver"));
const RConsumo = lazy(() => import("./reports/RConsumo"));
const RMontante = lazy(() => import("./reports/RMontante"));
const Rquantidade = lazy(() => import("./reports/Rquantidade"));

// accounting
const ContaRela = lazy(() => import("./accounting/ContaRela"));

// mobile apps management
const MobileAppsManager = lazy(() => import("./mobile/MobileAppsManager"));
const AppClientesConsole = lazy(() => import("./mobile/AppClientesConsole"));

// IRS — mesa de atendimento: a fila trata da entrada (abrir e encaminhar),
// o painel trata do acompanhamento (atribuir, confirmar e fechar).
const IrsQueue = lazy(() => import("./irs/IrsQueue"));
const IrsPanel = lazy(() => import("./irs/IrsPanel"));
const IrsClients = lazy(() => import("./irs/IrsClients"));
const IrsUnlock = lazy(() => import("./irs/IrsUnlock"));
const IrsTrends = lazy(() => import("./irs/IrsTrends"));

// shared
const DashboardOverview = lazy(() => import("./DashboardOverview"));
const Profile = lazy(() => import("./shared/Profile"));

// pages
const RecargaIsento200m3 = lazy(() => import("../pages/RecargaIsento200m3"));

// ── role configs ──────────────────────────────────────────────────────────────

const ROLES = {
  Operario: {
    pageTitle:  "Operações & Recargas",
    profilePath: "/home/Perfil",
    defaultKey:  "dashboard",
    navItems: (isSuperUser) => operarioNavItems(isSuperUser),
    keyPaths: {
      "dashboard": "/home",
      "1":    "/home/registar-cliente",
      "apps-moveis": "/home/apps-moveis",
      "gc-1": "/home/gest-cliente/pesquisar-cliente",
      "gc-2": "/home/gest-cliente/listar-cliente",
      "gc-3": "/home/gest-cliente/importar-clientes",
      "gc-4": "/home/gest-cliente/clientes-desconto",
      "gc-5": "/home/gest-cliente/clientes-isencao",
      "cnt-1": "/home/gest-contadores/importar-contador",
      "cnt-2": "/home/gest-contadores/pesquisar-contador",
      "cnt-3": "/home/gest-contadores/listar-contador",
      "24":   "/home/compras",
      "recargas-emitidas": "/home/recargas-emitidas",
      "224":  "/home/comprastictac",
      "reports": "/home/Relatorios_estatisticos",
      "31":   "/home/Relatorios_estatisticos",
    },
    routes: [
      { path: "/home", exact: true, component: DashboardOverview },
      { path: "/home/gest-contadores/importar-contador", exact: true, component: Importarcontador },
      { path: "/home/gest-contadores/pesquisar-contador", exact: true, component: Pesquisarcontador },
      { path: "/home/gest-contadores/listar-contador",    exact: true, component: ListaContador },
      // O operário acompanha a app dos clientes — tickets, adesões e
      // campanhas. A operação de campo é do técnico e da supervisão, e por
      // isso a consola abre aqui limitada a uma das duas apps.
      { path: "/home/apps-moveis", exact: true, component: AppClientesConsole },
      { path: "/home/gest-cliente/pesquisar-cliente", exact: true,    component: Pesquisarcliente },
      { path: "/home/gest-cliente/listar-cliente",    exact: true,    component: ListarCliente },
      { path: "/home/gest-cliente/importar-clientes", exact: true,    component: ClienteRegistro },
      { path: "/home/gest-cliente/clientes-desconto", exact: true,    component: ClientesDescontoPage },
      { path: "/home/gest-cliente/clientes-isencao",  exact: true,    component: ClientesIsencaoPage },
      { path: "/home/registar-cliente",                  exact: true, component: RegistarCliente },
      { path: "/home/gestvendas",                        exact: true, component: GestVendas },
      { path: "/home/gestpagamentos",                    exact: true, component: GestPagamentos },
      { path: "/home/gestrelatorios",                    exact: true, component: GestRelatorios },
      { path: "/home/Perfil",                            exact: true, component: Profile },
      { path: "/home/Pipeline",                          exact: true, component: PipeLine },
      { path: "/home/compras",                           exact: true, component: ComprasFeitas },
      { path: "/home/recargas-emitidas",                 exact: true, component: RecargasEmitidas },
      { path: "/home/comprasweb",                        exact: true, component: ComprasFeitasVitae },
      { path: "/home/comprastictac",                     exact: true, component: ComprasFeitasTictac },
      { path: "/home/isento",                            exact: true, component: RecargaIsento200m3 },
      { path: "/home/rollover",                          exact: true, component: RollOver },
      { path: "/home/Relatorios_estatisticos",           exact: true, component: HGestaoEstatisc },
      { path: "/home/Relatorios_servico",                exact: true, component: HRelatorioVenda },
      { path: "/home/Relatorios_estatisticos/volconsumo",  exact: true, component: VolumeConsumido },
      { path: "/home/Relatorios_estatisticos/montconsumo", exact: true, component: Montanteadq },
      { path: "/home/Relatorios_estatisticos/cosumocliente",   exact: true, component: ConsumoCliente },
      { path: "/home/Relatorios_estatisticos/consumoExcessivo", exact: true, component: ConsumoExcessivo },
      { path: "/home/Relatorios_estatisticos/consumoInaquado",  exact: true, component: ConsumoInadequado },
      { path: "/home/Relatorios_estatisticos/consumoSem",       exact: true, component: SemConsumo },
    ],
  },

  Director: {
    pageTitle:   "Painel de Administração",
    profilePath: "/admin/home/profile",
    defaultKey:  "dashboard",
    navItems: () => adminNavItems(),
    keyPaths: {
      "dashboard": "/admin/home",
      "1":   "/admin/home/gestVendas",
      "admin-recarga-test": "/admin/home/emitir-recarga",
      "admin-recargas-emitidas": "/admin/home/recargas-emitidas",
      "21":  "/admin/home/gestPagamento",
      "admin-gc-1": "/admin/home/gest-cliente/pesquisar-cliente",
      "admin-gc-2": "/admin/home/gest-cliente/listar-cliente",
      "admin-gc-3": "/admin/home/gest-cliente/importar-clientes",
      "admin-gc-4": "/admin/home/gest-cliente/clientes-desconto",
      "admin-gc-5": "/admin/home/gest-cliente/clientes-isencao",
      "admin-cnt-1": "/admin/home/gest-contadores/importar-contador",
      "admin-cnt-2": "/admin/home/gest-contadores/pesquisar-contador",
      "admin-cnt-3": "/admin/home/gest-contadores/listar-contador",
      "sts": "/admin/home/gestSTS",
      "taxSettings": "/admin/home/taxas-bairros",
      "fixedFeeNeighborhoods": "/admin/home/bairros-taxa-fixa",
      "meterBrands": "/admin/home/marcas-contador",
      "mobileApps": "/admin/home/apps-moveis",
      "users": "/admin/home/users",
      "reports": "/admin/home/relatorios",
      "31":  "/admin/home/relatorios",
      "32":  "/admin/home/relatorios",
      "33":  "/admin/home/relatorios",
    },
    routes: [
      { path: "/admin/home", exact: true, component: DashboardOverview },
      { path: "/admin/home/gestVendas",                              exact: true, component: AGestaVendas },
      { path: "/admin/home/emitir-recarga",                          exact: true, component: ComprarToken },
      { path: "/admin/home/recargas-emitidas",                       exact: true, component: RecargasEmitidas },
      // Caminho antigo, de quando isto era "recarga de teste". Mantido para
      // não partir links guardados nos favoritos de quem já o usava.
      { path: "/admin/home/recarga-test",                           exact: true, component: ComprarToken },
      { path: "/admin/home/gestPagamento",                           exact: true, component: AGestPagamentos },
      { path: "/admin/home/gest-cliente/pesquisar-cliente",          exact: true, component: Pesquisarcliente },
      { path: "/admin/home/gest-cliente/listar-cliente",             exact: true, component: ListarCliente },
      { path: "/admin/home/gest-cliente/importar-clientes",          exact: true, component: ClienteRegistro },
      { path: "/admin/home/gest-cliente/clientes-desconto",          exact: true, component: ClientesDescontoPage },
      { path: "/admin/home/gest-cliente/clientes-isencao",           exact: true, component: ClientesIsencaoPage },
      { path: "/admin/home/gest-contadores/importar-contador",       exact: true, component: Importarcontador },
      { path: "/admin/home/gest-contadores/pesquisar-contador",      exact: true, component: Pesquisarcontador },
      { path: "/admin/home/gest-contadores/listar-contador",         exact: true, component: ListaContador },
      { path: "/admin/home/taxas-bairros",                           exact: true, component: AdminTaxSettings },
      { path: "/admin/home/bairros-taxa-fixa",                       exact: true, component: FixedFeeNeighborhoods },
      { path: "/admin/home/marcas-contador",                         exact: true, component: MeterBrands },
      { path: "/admin/home/apps-moveis",                             exact: true, component: MobileAppsManager },
      { path: "/admin/home/users",                                    exact: true, component: AdminUsers },
      { path: "/admin/home/gestRelatorios",                          exact: true, component: AGestRelatorios },
      { path: "/admin/home/relatorios",                              exact: true, component: AdminReportsCenter },
      { path: "/admin/home/Relatorios_estatisticos",                 exact: true, component: GestaoEstatisc },
      { path: "/admin/home/Relatorios_servico",                      exact: true, component: RelatorioVenda },
      { path: "/admin/home/Relatorios_venda",                        exact: true, component: SalesReportsHub },
      { path: "/admin/home/Relatorios_estatisticos/volconsumo",      exact: true, component: VolumeConsumido },
      { path: "/admin/home/Relatorios_estatisticos/montconsumo",     exact: true, component: Montanteadq },
      { path: "/admin/home/Relatorios_estatisticos/cosumocliente",   exact: true, component: ConsumoCliente },
      { path: "/admin/home/Relatorios_estatisticos/consumoExcessivo",exact: true, component: ConsumoExcessivo },
      { path: "/admin/home/Relatorios_estatisticos/consumoInaquado", exact: true, component: ConsumoInadequado },
      { path: "/admin/home/Relatorios_estatisticos/consumoSem",      exact: true, component: SemConsumo },
      { path: "/admin/home/consumo",                                 exact: true, component: RConsumo },
      { path: "/admin/home/montante",                                exact: true, component: RMontante },
      { path: "/admin/home/quantidade",                              exact: true, component: Rquantidade },
      { path: "/admin/home/totalcliente",                            exact: true, component: ConsumoCliente },
      { path: "/admin/home/gestSTS",                                 exact: true, component: Geststs },
      { path: "/admin/home/profile",                                 exact: true, component: Profile },
    ],
  },

  // Mesa de atendimento: a interface central do fluxo de tickets. Tria o que
  // entra, encaminha para o comercial ou para o técnico, e é o único papel que
  // fecha tickets — sempre depois da confirmação do cliente.
  IRS: {
    pageTitle:   "Atendimento (IRS)",
    profilePath: "/irs/home/perfil",
    defaultKey:  "dashboard",
    navItems: () => irsNavItems(),
    keyPaths: {
      "dashboard": "/irs/home",
      "irs-queue": "/irs/home/fila",
      "irs-clientes": "/irs/home/clientes",
      "irs-compras": "/irs/home/compras",
      "irs-tendencias": "/irs/home/tendencias",
      "irs-desbloqueio": "/irs/home/desbloqueio",
    },
    routes: [
      { path: "/irs/home", exact: true, component: IrsPanel, title: "Painel de Atendimento" },
      { path: "/irs/home/fila", exact: true, component: IrsQueue, title: "Fila de Triagem" },
      { path: "/irs/home/clientes", exact: true, component: IrsClients, title: "Pesquisar Cliente" },
      { path: "/irs/home/compras", exact: true, component: ComprasFeitas, title: "Gestão de Compras" },
      { path: "/irs/home/tendencias", exact: true, component: IrsTrends, title: "Tendências de Atendimento" },
      { path: "/irs/home/desbloqueio", exact: true, component: IrsUnlock, title: "Desbloquear Contador" },
      { path: "/irs/home/perfil", exact: true, component: Profile, title: "Perfil" },
    ],
  },

  Contabilidade: {
    pageTitle:   "Contabilidade",
    profilePath: "/cont/home/perfil",
    defaultKey:  "dashboard",
    navItems: () => contabilidadeNavItems(),
    keyPaths: {
      "dashboard": "/cont/home",
      "1": "/cont/home/relatorio",
    },
    routes: [
      { path: "/cont/home", exact: true, component: DashboardOverview },
      { path: "/cont/home/relatorio", exact: true, component: ContaRela },
      { path: "/cont/home/perfil", exact: true, component: Profile },
    ],
  },

  recargatest: {
    pageTitle:   "Recargas",
    profilePath: "/test/home/perfil",
    defaultKey:  "dashboard",
    navItems: () => recargatestNavItems(),
    keyPaths: {
      "dashboard": "/test/home",
      "1": "/test/home/Comprar-Token",
    },
    routes: [
      { path: "/test/home", exact: true, component: DashboardOverview },
      { path: "/test/home/Comprar-Token", exact: true, component: ComprarToken },
      { path: "/test/home/perfil", exact: true, component: Profile },
    ],
  },
};

/**
 * Returns the full config for a given grupo string.
 * Falls back to Operario if role is unknown.
 */
export function getRoleConfig(grupo) {
  const session = getSession();
  const isSuperUser = session.isSuperuser;
  const activeRole = primaryRole(session.roles.length ? session.roles : [grupo], grupo);
  const config = ROLES[activeRole] || ROLES.Operario;
  return {
    ...config,
    navItems: config.navItems(isSuperUser),
    routes: config.routes.map((route) => ({
      ...route,
      roles: route.roles || [activeRole || "Operario"],
    })),
  };
}
